"use client"

/**
 * SHOP-85: Client-side product rotation after hydration.
 *
 * Server (ISR) renders with a seeded deterministic set — good for SEO and first paint.
 * After hydration, this component re-shuffles the full product pool with Math.random()
 * so each visitor sees a unique selection. No extra API calls.
 *
 * Variants use string identifiers (not render functions) so this component
 * is safe to use from Server Components (no function props crossing the RSC boundary).
 */

import { HttpTypes } from "@medusajs/types"
import { useEffect, useState } from "react"
import { PetSelectFeaturedProducts } from "@modules/home/components/petselect/PetSelectFeaturedProducts"
import { ModernFeaturedProducts } from "@modules/home/components/modern/ModernFeaturedProducts"
import { SpotlightedProducts } from "@modules/home/components/spotlighted-products"

type RotatedProductsProps = {
  allProducts: HttpTypes.StoreProduct[]
  initialProducts: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
  count?: number
} & (
  | { variant: "petselect" }
  | { variant: "modern" }
  | { variant: "spotlighted"; title?: string; subtitle?: string }
)

function clientShuffle<T>(arr: T[]): T[] {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export function RotatedProducts(props: RotatedProductsProps) {
  const { allProducts, initialProducts, region, count = 8 } = props
  const [products, setProducts] = useState<HttpTypes.StoreProduct[]>(initialProducts)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (allProducts.length <= count) return
    setVisible(false)
    const t = setTimeout(() => {
      const shuffled = clientShuffle(allProducts).slice(0, count)
      setProducts(shuffled)
      setVisible(true)
    }, 150)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const inner = () => {
    switch (props.variant) {
      case "petselect":
        return <PetSelectFeaturedProducts products={products} region={region} />
      case "modern":
        return <ModernFeaturedProducts products={products} region={region} />
      case "spotlighted":
        return (
          <SpotlightedProducts
            products={products}
            region={region}
            title={props.title}
            subtitle={props.subtitle}
          />
        )
    }
  }

  return (
    <div style={{ transition: "opacity 0.15s ease-in-out", opacity: visible ? 1 : 0 }}>
      {inner()}
    </div>
  )
}
